import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, UserPlus, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AutocompleteInput from "../hris/AutocompleteInput";
import { useModalLayer } from "../../hooks/useModalGuard";
import { izinKartu, periksaBentukHp, rapikanNama } from "../../lib/identitasNota";

/**
 * Kolom "Nama Pelanggan" nota — dipakai BERSAMA Input Nota dan Edit Penjualan
 * (brief owner 22 Sep 2026, batch N1; dulu G2 15 Sep).
 *
 * Owner: "pilih nama pelanggan untuk B2B dan USER menggunakan dropdown ...
 * agar penamaan pelanggan dengan tempo bisa tetap konsisten sehingga dapat
 * dilakukan penagihan sekaligus tanpa takut typo". Surat Tagih Sekaligus
 * mengelompokkan piutang lewat KUNCI NAMA, jadi satu typo memecah tagihan.
 *
 * - Pelanggan umum: kolom ketik biasa (perilaku lama).
 * - B2B/USER: daftar dari Data Pelanggan (pencarian di SERVER, min 2 huruf),
 *   ikon dropdown, pesan saat hasil kosong (dibedakan dari pencarian GAGAL —
 *   "tidak ada hasil" yang sebenarnya galat adalah pencarian yang berbohong),
 *   dan tombol "+ Pelanggan baru" yang membuat kartu saat itu juga.
 * - Nota dalam lingkup kartu (bawaan: B2B/USER BERTEMPO) WAJIB memakai nama
 *   dari kartu — `perluKartu` dari pemanggil, server menegakkan hal yang sama
 *   (`routers/identitas_nota.py`). Membalik keputusan G2 "saran, bukan
 *   paksaan": jalan keluar "+ Pelanggan baru" kini ada.
 *
 * Semua tombol SELALU terlihat (bukan hover) — di tablet/HP tidak ada hover.
 */
export default function PilihPelangganNota({
  jenis, nama, onNama, onPilih, kartu, onLepas, noHp, aturan, perluKartu = false,
  testid = "input-pelanggan", className = "",
}) {
  const { can } = useAuth();
  const dropdown = ["b2b", "user"].includes(jenis || "umum");
  // Kunci izin dari server, digabung di lib murni supaya aturannya (buat =
  // buat DAN cari) diuji perilakunya lewat node, sama dengan
  // `identitas_nota.boleh_buat_kartu` di server.
  const { bolehCari, bolehBuat } = izinKartu(aturan, can);

  // Hasil pencarian per KETIKAN (bukan "yang terakhir datang"): jawaban yang
  // tiba belakangan untuk ketikan lama tidak boleh memunculkan pesan "tidak
  // ada hasil" untuk ketikan yang sekarang tertulis di kolom.
  const hasilRef = useRef({});
  const [, setVersi] = useState(0);
  const [fokus, setFokus] = useState(false);
  const [baru, setBaru] = useState(null);   // null = modal tertutup

  const cari = async (q) => {
    const kunci = (q || "").trim();
    try {
      const { data } = await api.get("/pelanggan/cari-untuk-nota", { params: { q: kunci, limit: 10 } });
      const rows = (data.items || []).map(p => ({ ...p, value: p.nama }));
      hasilRef.current[kunci] = { n: rows.length, gagal: false };
      return rows;
    } catch {
      hasilRef.current[kunci] = { n: 0, gagal: true };
      return [];
    } finally {
      setVersi(v => v + 1);
    }
  };
  const baris = (p) => (
    <div className="min-w-0 flex-1">
      <p className="text-slate-900 truncate font-medium">{p.nama}</p>
      <p className="text-[10px] text-slate-500 font-mono">
        {p.no_hp || "tanpa no HP"}
        {p.terakhir_datang ? ` · terakhir ${p.terakhir_datang}` : ""}
      </p>
    </div>
  );

  const kini = (nama || "").trim();
  const rec = kini.length >= 2 ? hasilRef.current[kini] : null;
  const inp = className || "w-full h-9 px-3 rounded-lg border border-slate-200 text-sm";

  if (!dropdown) {
    return (
      <input value={nama} onChange={e => onNama(e.target.value)} data-testid={testid} className={inp} />
    );
  }

  const bukaBaru = () => setBaru({ nama: rapikanNama(nama), no_hp: noHp || "", tanpa_hp: false,
                                   konfirmasi_mirip: false, sibuk: false, konflik: null, galat: "" });

  return (
    <div>
      {kartu ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 h-9"
             data-testid={`${testid}-kartu`}>
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="flex-1 min-w-0 truncate text-sm text-emerald-900" data-testid={testid}>
            {kartu.nama}
          </span>
          <button type="button" onClick={onLepas} data-testid={`${testid}-ganti`}
            className="text-xs font-medium text-emerald-800 underline flex-shrink-0">
            Ganti
          </button>
        </div>
      ) : bolehCari ? (
        <div className="relative" onFocus={() => setFokus(true)} onBlur={() => setFokus(false)}>
          <AutocompleteInput
            value={nama}
            onChange={onNama}
            onPick={p => onPilih({ nama: p.nama, no_hp: p.no_hp || "" })}
            fetcher={cari}
            minChars={2}
            renderRow={baris}
            placeholder="Ketik 2 huruf nama / No HP, lalu pilih…"
            data-testid={testid}
            className={`${inp} pr-8`}
          />
          <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-2.5 pointer-events-none"
                       data-testid={`${testid}-chevron`} />
        </div>
      ) : (
        <input value={nama} onChange={e => onNama(e.target.value)} data-testid={testid} className={inp} />
      )}

      {!kartu && bolehCari && fokus && kini.length < 2 && (
        <p className="text-[11px] text-slate-500 mt-1" data-testid={`${testid}-petunjuk`}>
          Ketik minimal 2 huruf nama atau No HP untuk mencari di Data Pelanggan.
        </p>
      )}
      {!kartu && rec?.gagal && (
        <p className="text-[11px] text-rose-700 mt-1" data-testid={`${testid}-gagal`}>
          Pencarian Data Pelanggan gagal atau tidak diizinkan untuk akun ini — ini BUKAN berarti
          pelanggannya tidak ada.
        </p>
      )}
      {!kartu && rec && !rec.gagal && rec.n === 0 && (
        <p className="text-[11px] text-amber-800 mt-1" data-testid={`${testid}-kosong`}>
          Tidak ada pelanggan yang cocok dengan &ldquo;{kini}&rdquo;.
          {!bolehBuat && " Minta kasir/SPV menambahkan pelanggan ini ke Data Pelanggan."}
        </p>
      )}
      {!kartu && rec && !rec.gagal && rec.n >= 10 && (
        <p className="text-[11px] text-slate-500 mt-1" data-testid={`${testid}-banyak`}>
          Hanya 10 teratas yang tampil — ketik lebih lengkap.
        </p>
      )}
      {!kartu && perluKartu && (
        <p className="text-[11px] text-sky-800 mt-1" data-testid={`${testid}-wajib-kartu`}>
          {bolehCari
            ? "Nota bertempo: nama WAJIB dipilih dari Data Pelanggan supaya tagihannya tidak terpecah karena salah ketik."
            : "Akun ini tidak punya izin memilih pelanggan dari Data Pelanggan — nota B2B/USER bertempo tidak bisa dibuat dari akun ini."}
        </p>
      )}
      {!kartu && bolehBuat && (
        <button type="button" onClick={bukaBaru} data-testid={`${testid}-baru`}
          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
          <UserPlus className="w-3.5 h-3.5" /> + Pelanggan baru
        </button>
      )}

      {/* Portal ke <body>: form pemanggil membungkus kolom ini dengan <label>,
          dan klik di bagian modal yang bukan tombol akan "diteruskan" browser ke
          kontrol pertama di label itu (mis. tombol Ganti). */}
      {baru && createPortal(
        <ModalPelangganBaru
          isi={baru} setIsi={setBaru}
          onPakai={(k) => { setBaru(null); onPilih({ nama: k.nama, no_hp: k.no_hp || "" }); }}
        />, document.body)}
    </div>
  );
}

/** Modal "+ Pelanggan baru" — `POST /pelanggan/untuk-nota`.
 *
 *  409 bukan jalan buntu: nomor yang sudah dimiliki kartu lain menawarkan
 *  "Pakai kartu itu" ATAU "buat tanpa No HP" (mis. pemilik dealer yang dulu
 *  belanja atas nama pribadi), dan nama yang mirip menawarkan kartu yang ada
 *  SEBELUM "Tetap buat" — kartu typo yang lahir di sini akan menjadi ejaan
 *  "resmi" semua nota berikutnya. */
function ModalPelangganBaru({ isi, setIsi, onPakai }) {
  useModalLayer(true);
  const ubah = (x) => setIsi(s => ({ ...s, ...x }));
  const kirim = async (tambahan = {}) => {
    const muatan = { ...isi, ...tambahan };
    const nama = rapikanNama(muatan.nama);
    const hp = periksaBentukHp(muatan.no_hp);
    if (!nama) { ubah({ galat: "Nama pelanggan wajib diisi." }); return; }
    if (!hp.sah) { ubah({ galat: hp.pesan }); return; }
    if (!hp.simpan) { ubah({ galat: "No HP wajib diisi untuk kartu pelanggan baru." }); return; }
    ubah({ ...tambahan, sibuk: true, galat: "" });
    try {
      const { data } = await api.post("/pelanggan/untuk-nota", {
        nama, no_hp: muatan.no_hp, tanpa_hp: !!muatan.tanpa_hp,
        konfirmasi_mirip: !!muatan.konfirmasi_mirip,
      });
      toast.success(data?.message || "Pelanggan ditambahkan");
      // Kartu tanpa HP: nomor tetap dipakai di nota (milik kartu lain).
      onPakai({ nama: data.item.nama, no_hp: data.item.no_hp || muatan.no_hp });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.kode) {
        ubah({ sibuk: false, konflik: d, galat: d.kode === "hp_terdaftar" || d.kode === "nama_mirip" ? "" : d.pesan });
      } else {
        ubah({ sibuk: false, galat: (typeof d === "string" && d) || "Gagal menambah pelanggan" });
      }
    }
  };
  const k = isi.konflik;
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4"
         data-testid="pelanggan-baru-modal" onClick={() => setIsi(null)}>
      <div className="bg-white rounded-xl w-full max-w-md p-4 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-slate-900">Pelanggan baru</h4>
          <button type="button" onClick={() => setIsi(null)} data-testid="pelanggan-baru-batal"
            className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <label className="block text-xs text-slate-600">Nama pelanggan / usaha *
          <input value={isi.nama} onChange={e => ubah({ nama: e.target.value, konflik: null, konfirmasi_mirip: false })}
            data-testid="pelanggan-baru-nama"
            className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        <label className="block text-xs text-slate-600">No HP *
          <input value={isi.no_hp} onChange={e => ubah({ no_hp: e.target.value, konflik: null, tanpa_hp: false })}
            data-testid="pelanggan-baru-hp" placeholder="0812 3456 7890"
            className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        {isi.galat && <p className="text-xs text-rose-700" data-testid="pelanggan-baru-galat">{isi.galat}</p>}

        {k?.kode === "hp_terdaftar" && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-2" data-testid="pelanggan-baru-hp-terdaftar">
            <p className="text-xs text-amber-900">{k.pesan}</p>
            {(k.kartu || []).map((x, i) => (
              <button key={`${x.nama}-${i}`} type="button" onClick={() => onPakai(x)}
                data-testid={`pelanggan-baru-pakai-hp-${i}`}
                className="w-full text-left text-xs px-2 py-1.5 rounded border border-amber-300 bg-white">
                Pakai kartu <b>{x.nama}</b> {x.no_hp && <span className="font-mono">· {x.no_hp}</span>}
              </button>
            ))}
            <button type="button" disabled={isi.sibuk} onClick={() => kirim({ tanpa_hp: true })}
              data-testid="pelanggan-baru-tanpa-hp"
              className="w-full text-xs px-2 py-1.5 rounded bg-amber-600 text-white disabled:opacity-50">
              Buat kartu &ldquo;{rapikanNama(isi.nama)}&rdquo; tanpa No HP
            </button>
          </div>
        )}
        {k?.kode === "nama_mirip" && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-2 space-y-2" data-testid="pelanggan-baru-mirip">
            <p className="text-xs text-sky-900">{k.pesan}</p>
            <p className="text-[11px] text-sky-800">Mungkin maksud Anda:</p>
            {(k.kandidat || []).map((x, i) => (
              <button key={`${x.nama}-${i}`} type="button" onClick={() => onPakai(x)}
                data-testid={`pelanggan-baru-pakai-${i}`}
                className="w-full text-left text-xs px-2 py-1.5 rounded border border-sky-300 bg-white">
                Pakai <b>{x.nama}</b> {x.no_hp && <span className="font-mono">· {x.no_hp}</span>}
              </button>
            ))}
            <button type="button" disabled={isi.sibuk} onClick={() => kirim({ konfirmasi_mirip: true })}
              data-testid="pelanggan-baru-tetap"
              className="w-full text-xs px-2 py-1.5 rounded bg-sky-700 text-white disabled:opacity-50">
              Tetap buat kartu baru (orang/usaha yang berbeda)
            </button>
          </div>
        )}

        {!k && (
          <button type="button" disabled={isi.sibuk} onClick={() => kirim()}
            data-testid="pelanggan-baru-simpan"
            className="w-full h-9 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">
            {isi.sibuk ? "Menyimpan…" : "Simpan ke Data Pelanggan"}
          </button>
        )}
      </div>
    </div>
  );
}
